import React, { useState, useMemo, useCallback } from "react";
import { Checkbox } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";

import { getMassDisabled } from "../../../../utils/formViews";
import { getRolesSapSearchHelp, getRoles1CSearchHelp, getRolesOtherSearchHelp } from "../../../../utils/searchHelps";

import { createRowCellContentInner } from "../../../common/table";
import { TrashIcon } from "../../../common/icons";
import { SearchHelpInput } from "../../../common/form-controls";
import SearchHelpDialog from "../../../common/search-helps";
import { EntityFormViewTable } from "../../../common/entity-form";
import "./styles.less";

const getErrorFromValidationErrors = (row, columnId) => {
    return row && row[`error_${columnId}`];
};

const RolesView = ({
    systemId,
    systemType,
    isGroup,
    roles,
    addRole,
    deleteRole,
    deleteRoleMass,
    editRole,
    disabled,
    filterData,
    setFilterData,
    searchString,
    setSearchString,
    getDialogFilters,
    errorIndex,
    setErrorScroll,
}) => {
    const intl = useIntl();

    const [selectedIds, setSelectedIds] = useState([]);
    const [rowAction, setRowAction] = useState(null);

    const onChangeCell = useCallback((row, column) => (e) => {
        const value = e.target.value;
        editRole(row, column.id, value, systemType);
    }, [editRole, systemType])

    const onOpenSearchHelp = useCallback(
        (row, column) => () => {
            setRowAction({
                type: "search-help",
                row: row,
                field: column.id,
            });
        },
        []
    );

    const createRowCellContent = useCallback(
        (row, column, selected, onSelect) => {
            const error = getErrorFromValidationErrors(row, column.id);

            switch (column.type) {
                case "search-help": {
                    let hasError = error ? row[column.id]?.toLowerCase() === error.value?.toLowerCase() && error["type"] === "validation" : false;

                    if (error && error.code === "duplicate") {
                        hasError = true;
                    }

                    return (
                        <SearchHelpInput
                            disabled={disabled}
                            error={hasError}
                            errorMessage={error && error.message}
                            warning={
                                error
                                    ? row[column.id]?.toLowerCase() === error.value?.toLowerCase() && error["type"] === "integration_warning"
                                    : false
                            }
                            warningMessage={error && error.message}
                            showFormHelpers={false}
                            showInnerHelpers
                            value={row[column.id]}
                            fullWidth
                            onOpenSearchHelp={onOpenSearchHelp(row, column)}
                            onChange={onChangeCell(row, column)}
                        />
                    );
                }

                case "boolean":
                    return (
                        <Checkbox
                            disabled={disabled}
                            checked={row[column.id]}
                            sx={{
                                "&.Mui-checked": {
                                    color: "gray",
                                },
                            }}
                            onClick={() => {
                                editRole(row, column.id, !row[column.id], systemType);
                            }}
                        />
                    );

                default:
                    return createRowCellContentInner(row, column, selected, onSelect);
            }
        },
        [disabled, editRole, onChangeCell, onOpenSearchHelp, systemType]
    );

    const columnTexts = useMemo(
        () => ({
            role: intl.formatMessage({ id: "function-form-page.subtable-column.role" }),
            description: intl.formatMessage({ id: "function-form-page.subtable-column.description" }),
            active: intl.formatMessage({ id: "function-form-page.subtable-column.active" }),
        }),
        [intl]
    );


    const columns = useMemo(
        () => [
            {
                id: "select",
                type: "select",
            },
            {
                id: "role",
                title: columnTexts.role,
                type: "search-help",
                filterable: true,
            },
            {
                id: "description",
                title: columnTexts.description,
                type: "text",
                width: "40%",
            },
            {
                id: "active",
                title: columnTexts.active,
                type: "boolean",
            },
            {
                id: "actions",
                type: "actions",
                actions: [
                    {
                        id: "delete",
                        disabled,
                        IconComponent: TrashIcon,
                        onClick: (_, row) => deleteRole(row.id),
                    },
                ],
            },
        ],
        [columnTexts, deleteRole, disabled]
    );

    const toolbarActions = useMemo(() => {
        const massDisabled = getMassDisabled(disabled, selectedIds);

        return [
            {
                id: "mass-delete",
                title: <FormattedMessage id="common.btn-delete" />,
                callback: () => {
                    deleteRoleMass(selectedIds);
                    setSelectedIds([]);
                },
                position: "end",
                variant: "contained",
                disabled: massDisabled,
            },
            {
                id: "search",
                type: "search",
                position: "end",
                value: searchString,
                callback: setSearchString,
            },
            {
                id: "add",
                title: <FormattedMessage id="role-view.btn-create-role" />,
                callback: () => {
                    setSearchString("");
                    setFilterData([])
                    addRole();
                },
                position: "begin",
                disabled,
            },
        ];
    }, [addRole, disabled, searchString, setSearchString, selectedIds, deleteRoleMass, setFilterData]);

    const getSearchHelpData = useCallback((params, signal) => {
        switch (systemType) {
            case "SAP":
                return getRolesSapSearchHelp(params, signal, systemId, isGroup);

            case "1C":
                return getRoles1CSearchHelp(params, signal, systemId, isGroup);

            case "OTHER":
                return getRolesOtherSearchHelp(params, signal, systemId, isGroup);
        }
    }, [systemId, isGroup, systemType]);

    const searchHelpColumns = {
        role: [
            { id: "key", title: columnTexts.role },
            { id: "text", title: columnTexts.description },
        ],
    };

    const handleSelectRow = useCallback(
        (row, value) => {
            if (value) {
                setSelectedIds([...selectedIds, row.id]);
                return;
            }

            setSelectedIds(selectedIds.filter((id) => id !== row.id));
        },
        [selectedIds]
    );

    const handleSelectAll = useCallback(
        (value) => {
            if (!value) {
                setSelectedIds([]);
                return;
            }

            setSelectedIds(roles.map((role) => role.id));
        },
        [roles]
    );

    return (
        <>
            <EntityFormViewTable
                className="roles-view-table "
                rows={roles}
                columns={columns}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                createRowCellContent={createRowCellContent}
                filterData={filterData}
                setFilterData={setFilterData}
                getFilters={getDialogFilters}
                errorIndex={errorIndex}
                setErrorScroll={setErrorScroll}
                searchString={searchString}
                setSearchString={setSearchString}
            />

            {rowAction?.type === "search-help" && (
                <SearchHelpDialog
                    defaultSearchString={rowAction.row[rowAction.field] || ""}
                    open={rowAction && rowAction.type === "search-help"}
                    readRows={getSearchHelpData}
                    columns={searchHelpColumns[rowAction.field]}
                    selectOneRow={true}
                    onSubmit={(selectedRow) => {
                        editRole(rowAction.row, rowAction.field, selectedRow.key, systemType);
                        setRowAction(null);
                    }}
                    onCancel={() => {
                        setRowAction(null);
                    }}
                />
            )}
        </>
    );
};

RolesView.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.object),
    addRole: PropTypes.func,
    deleteRole: PropTypes.func,
    deleteRoleMass: PropTypes.func,
    editRole: PropTypes.func,
    disabled: PropTypes.bool,
    validationErrors: PropTypes.object,
    systemId: PropTypes.string,
    systemType: PropTypes.string,
    tableRef: PropTypes.any,
    filterData: PropTypes.arrayOf(PropTypes.object),
    setFilterData: PropTypes.func,
    searchString: PropTypes.string,
    setSearchString: PropTypes.func,
    getDialogFilters: PropTypes.func,
    errorIndex: PropTypes.number,
    setErrorScroll: PropTypes.func,
    isGroup: PropTypes.bool,
};

export default RolesView;
